<template>
  <!-- This is a fork of github.com/DomonJi/vue-clock-picker -->
  <!-- A11y is questionable here. Is it a "button"? -->
  <div class="time-picker-container">
    <div
      class="time-picker-preview"
      @click="toggleFocus"
      @keydown.enter="toggleFocus"
      :class="{ active: focused, 'disabled-input': disabled }"
      tabindex="0"
      role="button"
      @focusin="overElement = true"
      @focusout="overElement = false"
      @mouseover="overElement = true"
      @mouseout="overElement = false">
      <div class="preview-container">
        <span class="clock"><font-awesome-icon :icon="faIcons.faClock" /></span>
        <span class="time-span">
          {{ hourString }}:{{ minuteString }} {{ amString }}
        </span>
      </div>
    </div>

    <div class="handler-container">
      <outside-click-handler
        :on-outside-click="onClearFocus"
        :focused="focused">
        <time-picker-modal
          :init-hour="hour"
          :init-minute="minute"
          :clearModal="onClearFocus"
          :handle-hour-change="handleHourChange"
          :handle-minute-change="handleMinuteChange"
          :handle-time-change="handleTimeChange"
          v-if="focused">
          <template #modal></template>
        </time-picker-modal>
      </outside-click-handler>
    </div>
  </div>
</template>

<script>
import OutsideClickHandler from 'components/Widgets/ClockPicker/OutsideClickHandler';
import TimePickerModal from 'components/Widgets/ClockPicker/TimePickerModal';

export default {
  components: {
    OutsideClickHandler,
    TimePickerModal,
  },

  beforeUnmount() {
    if (this.uniqueIdLocal && this.dispenseGlobalProp) {
      this.dispenseGlobalProp.unsubscribe(this.uniqueIdLocal);
    }
  },

  computed: {
    amString() {
      return this.hour < 12 ? 'AM' : 'PM';
    },
    hourString() {
      if (this.hour === 0) {
        return '12';
      }
      if (this.hour < 13) {
        return String(this.hour);
      }
      return String(this.hour - 12);
    },
    minuteString() {
      return this.minute < 10 ? `0${this.minute}` : String(this.minute);
    },
  },

  created() {
    if (this.dispenseGlobalProp) {
      this.uniqueIdLocal = this.name;
      if (this.uniqueId) {
        this.uniqueIdLocal = this.uniqueId;
      }

      this.dispenseGlobalProp.subscribe(this.uniqueIdLocal, async (val) => {
        switch (val.name) {
          case 'hourset': {
            await this.handleParentHour(val.value);
            break;
          }
          case 'minuteset': {
            await this.handleParentMinute(val.value);
            break;
          }
          default: {
            throw Error(
              'unknown message from parent component: ' +
                `${JSON.stringify(val)}`,
            );
          }
        }
      });
    }
  },

  data() {
    return {
      hour: this.defaultHour,
      minute: this.defaultMinute,
      focused: this.defaultFocused,
      overElement: false,
      uniqueIdLocal: '',
    };
  },

  emits: ['emitTimeObj'],

  methods: {
    onFocus() {
      this.focused = true;
      if (this.onFocusChange) this.onFocusChange(true);
    },
    onClearFocus() {
      if (!this.overElement) this.focused = false;
      if (!this.overElement && this.onFocusChange) this.onFocusChange(false);
    },
    toggleFocus() {
      if (this.disabled) return;
      this.focused = !this.focused;
      if (this.onFocusChange) this.onFocusChange(this.focused);
    },
    handleHourChange(hour) {
      this.hour = hour;
      if (this.onHourChange) this.onHourChange(this.hour);
      this.handleTimeChange({
        hour: this.hour,
      });
    },
    handleMinuteChange(minute) {
      this.minute = minute;
      if (this.onMinuteChange) this.onMinuteChange(this.minute);
      this.handleTimeChange({
        minute: this.minute,
      });
    },
    async handleParentHour(hour) {
      this.hour = hour;
    },
    async handleParentMinute(minute) {
      this.minute = minute;
    },
    handleTimeChange(timeObj) {
      this.hour = timeObj['hour'] ? timeObj['hour'] : this.hour;
      this.minute = timeObj['minute'] ? timeObj['minute'] : this.minute;
      if (this.onTimeChange) {
        this.onTimeChange({
          hour: this.hour,
          minute: this.minute,
        });
      }
    },
  },

  props: {
    mode: {
      type: [String, Number],
      default: '24',
      validator: (value) => value === 24 || value === 12,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    defaultHour: {
      type: [String, Number],
      default: new Date().getHours(),
      validator: (value) => value >= 0 && value <= 24,
    },
    defaultMinute: {
      type: [String, Number],
      default: new Date().getMinutes(),
      validator: (value) => value >= 0 && value < 60,
    },
    defaultFocused: {
      type: Boolean,
      default: false,
    },
    onFocusChange: {
      type: Function,
      default: () => {},
    },
    onHourChange: {
      type: Function,
      default: () => {},
    },
    onMinuteChange: {
      type: Function,
      default: () => {},
    },
    onTimeChange: {
      type: Function,
      default: () => {},
    },
    parentHour: {
      type: Number,
    },
    parentMinute: {
      type: Number,
    },
    uniqueId: {
      type: String,
      required: false,
    },
  },

  watch: {
    focused: {
      handler: function handle(focus) {
        if (!focus) {
          this.$emit('emitTimeObj', {
            hour: this.hour,
            minute: this.minute,
          });
        }
      },
    },
  },
};
</script>

<style lang="scss">
.time-picker-container {
  width: 193px;
  position: relative;
}

.time-picker-preview {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #bfcbd9;
  color: #1f2d3d;
  display: block;
  height: 36px;
  line-height: 1;
  outline: 0;
  padding: 3px 10px;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 100%;
  margin-bottom: 3px;
}

.time-picker-preview:hover {
  border-color: #8391a5;
  // box-shadow: 0 8px 8px 0 rgba(0, 0, 0, 0.12),
  // 0 0 8px 0 rgba(0, 0, 0, 0.08);
}

.time-picker-preview.active {
  outline: 0;
  border-color: #2f97e9;
}

.disabled-input {
  background-color: #eef1f6;
  border-color: #d1dbe5;
  color: #bbb;
  cursor: not-allowed;
}

.preview-container {
  position: absolute;
  top: 52%;
  left: 19%;
  height: 40px;
  line-height: 40px;
  /*padding-left: 30px;*/
  transform: translate(-50%, -50%);
  font-size: 13.3333px;
}

.time-span {
  top: 0;
  left: -5px;
  position: absolute;
  width: 193px;
  text-align: left;
  transition: all 0.3s;
}

.clock {
  top: 0;
  left: -29px;
  position: absolute;
  width: 20px;
  text-align: center;
  color: #c0c4cc;
  transition: all 0.3s;
}

.handler-container {
  position: static;
}
</style>
